'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';

import {
  API_BASE_URL,
  clearStoredToken,
  getStoredToken,
} from '@/lib/api-client';

type User = {
  id: number;
  email: string;
  created_at: string;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const redirectingRef = useRef(false);

  const handleUnauthorized = useCallback(() => {
    clearStoredToken();
    setUser(null);
    if (
      typeof window !== 'undefined' &&
      pathname !== '/login' &&
      !redirectingRef.current
    ) {
      redirectingRef.current = true;
      router.push('/login');
    }
  }, [pathname, router]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const token = getStoredToken();

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleUnauthorized();
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Auth refresh failed (${response.status})`);
      }

      const body = (await response.json()) as User;
      setUser(body);
      redirectingRef.current = false;
    } catch (error) {
      console.error('Failed to refresh session', error);
      handleUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    clearStoredToken();
    setUser(null);
    if (!redirectingRef.current) {
      redirectingRef.current = true;
      router.push('/login');
    }
  }, [router]);

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      signOut,
    }),
    [user, loading, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

